import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'

interface GoogleAuthState {
  isGoogleLinked: boolean
  googleEmail?: string
  calendarAccess: boolean
  lastSync?: string
}

export default function GoogleAuthIntegration() {
  const [googleAuth, setGoogleAuth] = useState<GoogleAuthState>({
    isGoogleLinked: undefined as any, // undefined means not loaded yet
    calendarAccess: false
  })
  const [isLoading, setIsLoading] = useState(false)
  
  const { user } = useAuth()


  useEffect(() => {
    loadAuthStatus()
  }, [])

  // Retry loading auth status when user becomes available
  useEffect(() => {
    if (user && googleAuth.isGoogleLinked === undefined) {
      console.log('User is now available, retrying Google auth status...')
      loadAuthStatus()
    }
  }, [user, googleAuth.isGoogleLinked])

  // Force refresh auth status when component becomes visible (for cache issues)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        console.log('Page became visible, refreshing Google auth status...')
        loadAuthStatus()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [user])

  const loadAuthStatus = async () => {
    try {
      console.log('Loading Google auth status...')
      // Add cache-busting parameter
      const response = await apiClient.getUserGoogleAuth(`?t=${Date.now()}`)
      console.log('Google auth status response:', response)
      setGoogleAuth(response)
    } catch (error: any) {
      console.log('Error loading Google auth status:', error.response?.status, error.response?.data)
      if (error.response?.status === 403) {
        console.log('User not authenticated yet, will retry when user is available')
      } else {
        console.log('No Google auth configured yet')
        // Set default state so UI shows properly
        setGoogleAuth({
          isGoogleLinked: false,
          calendarAccess: false
        })
      }
    }
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    try {
      // Add timestamp to prevent cache issues
      const timestamp = Date.now()
      const redirectUri = window.location.origin + `/dashboard?google_link=true&t=${timestamp}`

      // Fetch the OAuth authorization URL from the Go backend
      const response = await fetch(`/api/auth/google/init?redirect_uri=${encodeURIComponent(redirectUri)}`)
      const data = await response.json()

      if (data.authorization_url) {
        toast.success('Redirecting to Google for calendar access...')
        window.location.href = data.authorization_url
      } else {
        toast.error('Failed to initialize Google authentication')
        setIsLoading(false)
      }

    } catch (error: any) {
      toast.error('Failed to start Google authentication')
      console.error('Google auth error:', error)
      setIsLoading(false)
    }
  }

  const handleSyncCalendar = async () => {
    if (!googleAuth.isGoogleLinked) {
      toast.error('Please link your Google account first')
      return
    }
    
    setIsLoading(true)
    try {
      const result = await apiClient.syncGoogleCalendar()
      toast.success(`Synced ${result.events_imported} calendar events!`)
      setGoogleAuth(prev => ({ ...prev, lastSync: new Date().toISOString() }))
    } catch (error: any) {
      toast.error('Failed to sync calendar')
    }
    setIsLoading(false)
  }

  const handleUnlinkGoogle = async () => {
    if (!confirm('Are you sure you want to unlink your Google account? This will disable calendar sync.')) {
      return
    }
    
    setIsLoading(true)
    try {
      await apiClient.unlinkGoogleAuth()
      setGoogleAuth({
        isGoogleLinked: false,
        calendarAccess: false
      })
      toast.success('Google account unlinked successfully')
    } catch (error: any) {
      toast.error('Failed to unlink Google account')
    }
    setIsLoading(false)
  }

  return (
    <div className="space-y-6">
      {/* Google Authentication Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">🔐</div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Google Authentication</h3>
              <p className="text-sm text-gray-600">Link your Google account for easier login and calendar access</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            googleAuth.isGoogleLinked 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {googleAuth.isGoogleLinked ? '✅ Linked' : '❌ Not Linked'}
          </div>
        </div>

        {!googleAuth.isGoogleLinked ? (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Benefits of Google Integration:</h4>
              <ul className="text-blue-800 text-sm space-y-1 list-disc list-inside">
                <li>One-click sign-in with your Google account</li>
                <li>Automatic calendar sync with family events</li>
                <li>Secure authentication through Google</li>
                <li>Access to Google Family Calendar data</li>
              </ul>
            </div>
            
            <button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="btn-primary flex items-center space-x-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{isLoading ? 'Connecting...' : 'Sign in with Google'}</span>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="text-xl">✅</div>
                <div>
                  <div className="font-medium text-green-900">
                    Connected to {googleAuth.googleEmail}
                  </div>
                  <div className="text-sm text-green-700">
                    Calendar access: {googleAuth.calendarAccess ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleUnlinkGoogle}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Unlink
              </button>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSyncCalendar}
                disabled={isLoading || !googleAuth.calendarAccess}
                className="btn-primary flex-1"
              >
                📅 {isLoading ? 'Syncing...' : 'Sync Calendar'}
              </button>
              
              {googleAuth.lastSync && (
                <div className="text-xs text-gray-500 flex items-center">
                  Last sync: {new Date(googleAuth.lastSync).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Family Integration Benefits */}
      <div className="card bg-purple-50 border-purple-200">
        <h3 className="font-semibold text-purple-900 mb-3 flex items-center">
          <span className="text-xl mr-2">🎉</span>
          Family Integration Benefits
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="font-medium text-purple-800">Google Calendar Sync:</div>
            <ul className="text-purple-700 space-y-1 list-disc list-inside ml-4">
              <li>Import existing family events</li>
              <li>Automatic chore scheduling around events</li>
              <li>Sync completed chores back to calendar</li>
            </ul>
          </div>
          <div className="space-y-2">
            <div className="font-medium text-purple-800">Easy Access:</div>
            <ul className="text-purple-700 space-y-1 list-disc list-inside ml-4">
              <li>One-click Google login</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-sm text-purple-800">
          To let a TV or tablet show HousePoints for the whole family, pair it in Settings &gt; Devices.
        </p>
      </div>
    </div>
  )
}
